import { useCallback, useRef } from 'react'
import { Box, Typography } from '@mui/material'
import { keyframes } from '@mui/system'
import { BUTTON_TRANSITION } from '../../theme/buttonHover'

const scrollDotMove = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(8px); }
`

function scrollToNextSection(fromEl) {
  let current = fromEl.parentElement
  while (current && current !== document.body) {
    const next = current.nextElementSibling
    if (next instanceof HTMLElement && next.offsetHeight > 0) {
      next.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
    current = current.parentElement
  }
}

export default function ScrollDownIndicator({
  light = false,
  sx = {},
  textSx = {},
  gap = '14px',
}) {
  const ref = useRef(null)
  const color = light ? '#fffefa' : '#111'

  const handleClick = useCallback(() => {
    if (ref.current) scrollToNextSection(ref.current)
  }, [])

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        handleClick()
      }
    },
    [handleClick],
  )

  return (
    <Box
      ref={ref}
      role="button"
      tabIndex={0}
      aria-label="Scroll to next section"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap,
        cursor: 'pointer',
        userSelect: 'none',
        transition: BUTTON_TRANSITION,
        ...sx,
      }}
    >
      <Typography
        sx={{
          fontSize: { xs: 18, md: 24 },
          lineHeight: 'normal',
          fontWeight: 400,
          color,
          ...textSx,
        }}
      >
        Scroll Down
      </Typography>
      <Box
        aria-hidden
        sx={{
          width: 18,
          height: 30,
          borderRadius: '50px',
          border: `1px solid ${color}`,
          position: 'relative',
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: 5,
            width: 10,
            height: 10,
            marginLeft: '-5px',
            borderRadius: '50px',
            bgcolor: color,
            animation: `${scrollDotMove} 1.8s ease-in-out infinite`,
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
            },
          }}
        />
      </Box>
    </Box>
  )
}
